import React from 'react';
import { useTranslation } from 'react-i18next';
import RoomSubColumn from './RoomSubColumn';
import type { Appointment, ClinicRoom } from '../../../services/clinicsService';

interface DayColumnProps {
  date: Date;
  rooms: ClinicRoom[];
  appointments: Appointment[];
  timeSlots: string[];
  daySched: any; // { isHoliday, isOpen, schedules, holiday }
  isToday: boolean;
  isSlotAvailable: (timeStr: string) => boolean;
  onSlotClick?: (date: Date, room: ClinicRoom) => void;
  onAppointmentClick?: (app: Appointment, e: React.MouseEvent) => void;
  onRoomClick?: (room: ClinicRoom) => void;
}

export default function DayColumn({
  date,
  rooms,
  appointments,
  timeSlots,
  daySched,
  isToday,
  isSlotAvailable,
  onSlotClick,
  onAppointmentClick,
  onRoomClick
}: DayColumnProps) {
  const { i18n } = useTranslation();
  
  // Use Intl.DateTimeFormat for dynamic locale-based formatting
  const dayName = new Intl.DateTimeFormat(i18n.language || 'es', { weekday: 'short' }).format(date);
  const dayNumber = new Intl.DateTimeFormat(i18n.language || 'es', { day: 'numeric' }).format(date);

  return (
    <div data-ui="day-column-region-001" className="flex-1 border-r border-white/10 relative flex flex-col">
      {/* Day Header (spans all rooms) */}
      <div data-ui="day-column-region-002" className={`h-12 border-b border-white/10 flex items-center justify-center gap-2 sticky top-0 bg-black/80 backdrop-blur-md z-40 font-semibold ${isToday ? 'text-blue-400' : 'text-gray-300'}`}>
        <span className="text-xs uppercase">{dayName}</span>
        <span className={`text-lg ${isToday ? 'font-bold bg-blue-500 text-white w-7 h-7 rounded-full flex items-center justify-center' : ''}`}>
          {dayNumber}
        </span>
      </div>

      {/* Holiday Banner */}
      {daySched.isHoliday && (
        <div data-ui="day-column-region-003" className="absolute top-12 left-0 right-0 bg-red-500/20 text-red-200 text-xs text-center py-1 z-30 border-b border-red-500/30 truncate px-1">
          {daySched.holiday?.descripcion || 'Festivo'}
        </div>
      )}

      {/* Rooms Container */}
      <div data-ui="day-column-region-004" className="flex flex-1">
        {rooms.map(room => {
          const roomAppointments = appointments.filter(app => app.room_id === room.id);
          return (
            <RoomSubColumn
              key={room.id}
              room={room}
              date={date}
              appointments={roomAppointments}
              timeSlots={timeSlots}
              isSlotAvailable={isSlotAvailable}
              onSlotClick={onSlotClick}
              onAppointmentClick={onAppointmentClick}
              onRoomClick={onRoomClick}
            />
          );
        })}
      </div>
    </div>
  );
}
